'use client';

import { c as _c } from "react/compiler-runtime";
import AudioPlayer from '@/components/component_unique/AudioPlayer';
import TweetEmbed from '@/components/component_unique/TweetEmbed';
import InstagramEmbed from '@/components/component_unique/InstagramEmbed';
import VideoEmbed from '@/components/component_unique/VideoEmbed';
import GumletEmbed from '@/components/component_unique/GumletEmbed';
import PdfFile from '@/components/component_unique/PdfFile';
import ChipsForm from '@/components/component_form/ChipsForm';
import JustIn from '@/components/component_unique/JustIn';
import VuukleComments from '@/components/ads/VuukleComments';
import YouTubeThumbnail from '@/components/component_unique/YouTubeThumbnail';
import FileAttachment from '@/components/component_unique/FileAttachment';
import PhotoCarousel from '@/components/component_unique/PhotoCarousel';
import { getImageUrl } from '@/lib/api';
import { processArticleLinks } from '@/lib/linkUtils';
import { sanitizeToText } from '@/lib/sanitize';
import { computeParagraphMap } from '@/lib/articleInlineAds';
export interface ArticleGumletVideoBodyProps {
  story: any;
  index: number;
  activeAdArticleIndex: number;
  category: string;
  imageUrl: string;
  updatedAt: string;
  showAudioPlayer: boolean;
  promotionBlock: React.ReactNode;
  renderInlineAd?: (globalIdx: number, kind?: 'text' | 'image') => React.ReactNode;
  showRelatedStory?: boolean;
  showPromotionalMessage?: boolean;
  showLinkedStories?: boolean;
  showStoryTags?: boolean;
  showComments?: boolean;
  /** Whether to render this component's own headline/subheadline block AND
   *  its own GumletEmbed/YouTubeThumbnail video. The dynamic article-template
   *  renderer passes false: ArticleHeroSection's heroImage field (gumletVideo
   *  mode) renders the video itself, falling back to this exact
   *  story['embed-js']/first-card-jsembed source when no gumletVideoUrl slot
   *  is bound — so rendering it again here would duplicate it. The author/
   *  date row and the body-loop dedup stay unconditional either way. */
  showHero?: boolean;
}

/**
 * The `gumlet-video` article body — extracted from the `story['story-template']
 * === 'gumlet-video'` branch in src/app/api/articles/ArticleListInfinite.tsx.
 * The defining feature vs. the generic text body: the Gumlet/YouTube video
 * (story-level embed-js, or the first jsembed found in cards) renders
 * prominently near the top, and the body loop explicitly skips re-rendering
 * that same first video element to avoid showing it twice.
 */
export default function ArticleGumletVideoBody(t0) {
  const $ = _c(58);
  const {
    story,
    index,
    activeAdArticleIndex,
    category,
    imageUrl,
    showAudioPlayer,
    promotionBlock,
    renderInlineAd,
    showRelatedStory: t1,
    showPromotionalMessage: t2,
    showLinkedStories: t3,
    showStoryTags: t4,
    showComments: t5,
    showHero: t6
  } = t0;
  const showRelatedStory = t1 === undefined ? true : t1;
  const showPromotionalMessage = t2 === undefined ? true : t2;
  const showLinkedStories = t3 === undefined ? true : t3;
  const showStoryTags = t4 === undefined ? true : t4;
  const showComments = t5 === undefined ? true : t5;
  const showHero = t6 === undefined ? true : t6;
  let t7;
  if ($[0] !== story.tags) {
    t7 = story.tags?.map(_temp);
    $[0] = story.tags;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const tagNames = t7;
  let inlineAlsoReadIds;
  if ($[2] !== story.cards) {
    inlineAlsoReadIds = new Set();
    story.cards?.forEach(card => {
      card["story-elements"]?.forEach(el => {
        if (el.subtype === "also-read") {
          const id = el.metadata?.["linked-story-id"] || el["linked-story-id"];
          if (id) {
            inlineAlsoReadIds.add(id);
          }
        }
      });
    });
    $[2] = story.cards;
    $[3] = inlineAlsoReadIds;
  } else {
    inlineAlsoReadIds = $[3];
  }
  let t8;
  if ($[4] !== story) {
    t8 = !story["embed-js"] ? story.cards?.flatMap(_temp3)?.[0] : null;
    $[4] = story;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const firstCardEmbed = t8;
  let t9;
  if ($[6] !== showHero || $[7] !== story.headline || $[8] !== story.subheadline) {
    t9 = showHero && <div className="p-4 border-b border-DCDCDC"><h1 className="text-24-tamil-700 text-1B1B1B mb-2">{story.headline}</h1>{story.subheadline && <h2 className="text-18-tamil-400 text-4D4D4D mb-4">{story.subheadline}</h2>}</div>;
    $[6] = showHero;
    $[7] = story.headline;
    $[8] = story.subheadline;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== showAudioPlayer) {
    t10 = showAudioPlayer && <AudioPlayer />;
    $[10] = showAudioPlayer;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== firstCardEmbed || $[13] !== imageUrl || $[14] !== showHero || $[15] !== story) {
    t11 = showHero && <><div className="border-b border-DCDCDC">{story["embed-js"] ? <GumletEmbed embedJs={story["embed-js"]} /> : firstCardEmbed ? <GumletEmbed embedJs={firstCardEmbed["embed-js"]} /> : null}</div>{story["embed-url"] && <div className="border-b border-DCDCDC"><YouTubeThumbnail embedUrl={story["embed-url"]} thumbnailUrl={imageUrl} title={story.headline} /></div>}</>;
    $[12] = firstCardEmbed;
    $[13] = imageUrl;
    $[14] = showHero;
    $[15] = story;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== renderInlineAd) {
    t12 = renderInlineAd?.(-1);
    $[17] = renderInlineAd;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== imageUrl || $[20] !== renderInlineAd || $[21] !== showPromotionalMessage || $[22] !== story) {
    const paraMap = computeParagraphMap(story);
    let firstVideoSkipped = false;
    t13 = story.cards?.map((card_1, cardIdx) => <div key={cardIdx}>{card_1["story-elements"]?.map((element, elementIdx) => {
        if (cardIdx === 0 && !firstVideoSkipped && element.type === "jsembed" && element["embed-js"]) {
          firstVideoSkipped = true;
          return null;
        }
        const paraInfo = paraMap[`${cardIdx}_${elementIdx}`];
        const isRegularText = element.text && element.subtype !== "summary" && element.subtype !== "also-read" && element.subtype !== "quote" && element.subtype !== "blockquote" && element.subtype !== "cta" && element.subtype !== "q-and-a" && element.subtype !== "bigfact" && element.subtype !== "blurb" && element.subtype !== "question" && element.type !== "youtube-video" && !element.metadata?.["promotional-message"];
        return <div key={elementIdx} className="mb-6">{element.type === "file" && element["content-type"] === "application/pdf" && <div className="mb-4"><PdfFile fileName={element["file-name"]} url={element.url} s3Key={element["s3-key"]} /></div>}{element.type === "jsembed" && element.subtype === "tweet" && <TweetEmbed embedJs={element["embed-js"]} />}{element.type === "jsembed" && element.subtype === "instagram" && <InstagramEmbed embedJs={element["embed-js"]} />}{element.type === "jsembed" && element.subtype !== "tweet" && element.subtype !== "instagram" && <VideoEmbed embedJs={element["embed-js"]} type={element.type} subtype={element.subtype} />}{element.type === "youtube-video" && element["embed-url"] && <YouTubeThumbnail embedUrl={element["embed-url"]} thumbnailUrl={imageUrl} title={element.title} />}{element.subtype === "summary" && element.text && <div><h3 className="text-18-tamil-700 text-1B1B1B mb-2">Summary</h3><div className="text-18-tamil-400 text-1B1B1B mb-4 article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
              __html: processArticleLinks(element.text)
            }} /></div>}{element.subtype === "quote" && element.text && <div className="my-4 quote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
            __html: processArticleLinks(element.text)
          }} />}{element.subtype === "blockquote" && element.text && <div className="my-4 blockquote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
            __html: processArticleLinks(element.text)
          }} />}{isRegularText && paraInfo && paraInfo.paragraphs.length > 0 && paraInfo.paragraphs.map((paraHtml, paraIdx) => {
            const globalIdx = paraInfo.startIndex + paraIdx;
            return <div key={`p-${paraIdx}`}><div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                __html: processArticleLinks(paraHtml)
              }} />{renderInlineAd?.(globalIdx)}</div>;
          })}{isRegularText && (!paraInfo || paraInfo.paragraphs.length === 0) && <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
            __html: processArticleLinks(element.text)
          }} />}{element.text && element.metadata?.["promotional-message"] && showPromotionalMessage && <div className="text-14-tamil-400 article-links [&_p]:mb-4" style={{
            color: "#00000099"
          }} dangerouslySetInnerHTML={{
            __html: processArticleLinks(element.text)
          }} />}{element.type === "file" && element["content-type"] !== "application/pdf" && element.url && element["file-name"] && <div className="mb-4"><FileAttachment fileName={element["file-name"]} url={element.url} fileSize={element.metadata?.["file-size"]} /></div>}{element.type === "composite" && element.subtype === "image-gallery" && element["story-elements"]?.length > 0 && (element.metadata?.type === "slideshow" ? <div className="my-4"><PhotoCarousel photos={element["story-elements"].filter(_temp4).map(_temp5)} maxHeight={400} /></div> : <div className="my-4 grid grid-cols-2 sm:grid-cols-3 gap-2">{element["story-elements"].map(_temp6)}</div>)}{element.type === "composite" && element.subtype === "references" && element["story-elements"]?.length > 0 && <div className="my-4 border border-DCDCDC rounded-lg p-4 bg-gray-50"><h4 className="text-16-tamil-700 text-1B1B1B mb-2">குறிப்புகள்</h4><ul className="space-y-2 list-disc pl-5">{element["story-elements"].map(_temp7)}</ul></div>}{element.subtype === "cta" && element.metadata?.["cta-url"] && <div className="my-4 text-center"><a href={element.metadata["cta-url"]} target={element.metadata["open-in-new-tab"] ? "_blank" : "_self"} rel="noopener noreferrer" className="inline-block px-6 py-3 bg-012061 text-white rounded-full text-16-tamil-600 hover:opacity-90 transition-opacity">{element.metadata["cta-title"] || element.text}</a></div>}{element.subtype === "q-and-a" && (element.metadata?.question || element.metadata?.answer) && <div className="my-4 space-y-3">{element.metadata?.question && <div style={{
              backgroundColor: "#E3F2FD",
              padding: "16px",
              borderRadius: "8px"
            }}><div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px"
              }}><span style={{
                  backgroundColor: "#1565C0",
                  color: "white",
                  fontWeight: "bold",
                  minWidth: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  fontSize: "16px",
                  flexShrink: 0
                }}>Q</span><div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                  flex: 1
                }} dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element.metadata.question)
                }} /></div></div>}{element.metadata?.answer && <div style={{
              backgroundColor: "#FFF3E0",
              padding: "16px",
              borderRadius: "8px"
            }}><div style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px"
              }}><span style={{
                  backgroundColor: "#E65100",
                  color: "white",
                  fontWeight: "bold",
                  minWidth: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  fontSize: "16px",
                  flexShrink: 0
                }}>A</span><div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-0" style={{
                  flex: 1
                }} dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element.metadata.answer)
                }} /></div></div>}</div>}{element.subtype === "question" && element.text && <div className="my-4" style={{
            backgroundColor: "#E3F2FD",
            padding: "16px",
            borderRadius: "8px"
          }}><div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px"
            }}><span style={{
                backgroundColor: "#1565C0",
                color: "white",
                fontWeight: "bold",
                minWidth: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                fontSize: "16px",
                flexShrink: 0
              }}>Q</span><div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                flex: 1
              }} dangerouslySetInnerHTML={{
                __html: processArticleLinks(element.text)
              }} /></div></div>}{element.subtype === "bigfact" && (element.metadata?.content || element.text) && <div className="my-6 text-center border-y border-DCDCDC py-6"><div className="text-32-tamil-700 text-012061">{element.metadata?.content}</div>{element.metadata?.attribution && <div className="mt-2 text-14-tamil-400" style={{
              color: "#00000099"
            }}>{element.metadata.attribution}</div>}</div>}{element.subtype === "blurb" && element.text && <div className="my-4 bg-gray-50 rounded-lg p-4 article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-600 [&_blockquote]:italic [&_blockquote]:text-1B1B1B" dangerouslySetInnerHTML={{
            __html: processArticleLinks(element.text)
          }} />}{element.subtype === "also-read" && element.text && (() => {
            const linkedStoryId = element.metadata?.["linked-story-id"] || element["linked-story-id"];
            const linkedStory = linkedStoryId && story["linked-stories"]?.[linkedStoryId] || element.metadata?.["linked-story"];
            if (linkedStory && linkedStory.slug) {
              return <a href={`/${linkedStory.slug}`} className="flex flex-col sm:flex-row gap-3 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow my-3 p-4">{linkedStory["hero-image-s3-key"] && <div className="w-full sm:w-[150px] h-[160px] sm:h-[100px] flex-shrink-0"><img src={getImageUrl(linkedStory["hero-image-s3-key"])} alt={linkedStory.headline} className="w-full h-full object-cover" /></div>}<div className="p-3 flex-1 flex items-center"><h4 className="text-16-tamil-700 text-1B1B1B">{linkedStory.headline}</h4></div></a>;
            }
            return null;
          })()}</div>;
      })}</div>);
    $[19] = imageUrl;
    $[20] = renderInlineAd;
    $[21] = showPromotionalMessage;
    $[22] = story;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = t13;
  let t15;
  if ($[24] !== inlineAlsoReadIds || $[25] !== showLinkedStories || $[26] !== story) {
    t15 = showLinkedStories && story["linked-stories"] && Object.keys(story["linked-stories"]).length > 0 && <div className="mb-6"><div className="grid grid-cols-1 gap-4">{Object.entries(story["linked-stories"]).filter(t16 => {
          const [key] = t16;
          return !inlineAlsoReadIds.has(key);
        }).map(_temp8)}</div></div>;
    $[24] = inlineAlsoReadIds;
    $[25] = showLinkedStories;
    $[26] = story;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t12 || $[29] !== t14 || $[30] !== t15) {
    t16 = <div className="p-4 space-y-6">{t12}{t14}{t15}</div>;
    $[28] = t12;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== index || $[33] !== showRelatedStory) {
    t17 = index === 0 && showRelatedStory && <JustIn limit={5} />;
    $[32] = index;
    $[33] = showRelatedStory;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== showStoryTags || $[36] !== tagNames) {
    t18 = showStoryTags && tagNames && tagNames.length > 0 && <div className="border-b border-DCDCDC"><ChipsForm chips={tagNames} /></div>;
    $[35] = showStoryTags;
    $[36] = tagNames;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== activeAdArticleIndex || $[39] !== category || $[40] !== imageUrl || $[41] !== index || $[42] !== showComments || $[43] !== story.authors?.[0]?.name || $[44] !== story.headline || $[45] !== story.id || $[46] !== story.slug || $[47] !== story.tags) {
    t19 = showComments && Math.abs(index - activeAdArticleIndex) <= 1 && <div style={{
      display: index === activeAdArticleIndex ? "block" : "none"
    }} aria-hidden={index !== activeAdArticleIndex}><p style={{
        fontSize: "12px",
        color: "#666",
        lineHeight: 1.6,
        padding: "12px",
        background: "#f9f9f9",
        borderRadius: "6px",
        borderLeft: "3px solid #012061",
        margin: "12px 16px"
      }}>பின்னூட்டத்தில் வெளியாகும் கருத்துகளுக்கு அவற்றைப் பதிவிடுவோரே முழுப் பொறுப்பு; அவை தினமணியின் கருத்துகளைப் பிரதிபலிக்கவில்லை.தனிநபர், சமூகம், மதம் அல்லது நாடு ஆகியவற்றுக்கு எதிராக அவமதிக்கிற அல்லது ஆபாசமான விதத்திலுள்ள எந்தவொரு கருத்தும் இந்திய அரசின் தகவல் தொழில்நுட்பக் கொள்கைப்படி தண்டனைக்குரிய குற்றம். இதுபோன்ற கருத்துகளுக்கு எதிராக உரிய சட்ட நடவடிக்கை எடுக்கப்படும்.</p><div className="border-b border-DCDCDC p-4"><VuukleComments key={`vuukle-${story.id}`} articleId={story.id} articleTitle={story.headline || ""} articleUrl={`https://www.dinamani.com/${story.slug}`} articleImage={imageUrl} articleTags={story.tags?.slice(0, 3).map(_temp9).join(",") || category} articleAuthor={story.authors?.[0]?.name || "Dinamani"} /></div></div>;
    $[38] = activeAdArticleIndex;
    $[39] = category;
    $[40] = imageUrl;
    $[41] = index;
    $[42] = showComments;
    $[43] = story.authors?.[0]?.name;
    $[44] = story.headline;
    $[45] = story.id;
    $[46] = story.slug;
    $[47] = story.tags;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== promotionBlock || $[50] !== t10 || $[51] !== t11 || $[52] !== t16 || $[53] !== t17 || $[54] !== t18 || $[55] !== t19 || $[56] !== t9) {
    t20 = <>{t9}{t10}{t11}{promotionBlock}{t16}{t17}{t18}{t19}</>;
    $[49] = promotionBlock;
    $[50] = t10;
    $[51] = t11;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
    $[56] = t9;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}
function _temp9(tag_0) {
  return tag_0.name;
}
function _temp8(t0) {
  const [key_0, linkedStory_0] = t0;
  return <a key={key_0} href={`/${linkedStory_0.slug}`} className="flex flex-col lg:flex-row gap-3 lg:gap-4 p-4 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow">{linkedStory_0["hero-image-s3-key"] && <div className="w-full lg:w-[200px] h-[200px] lg:h-[120px] flex-shrink-0"><img src={getImageUrl(linkedStory_0["hero-image-s3-key"])} alt={linkedStory_0.headline} className="w-full h-full object-cover" /></div>}<div className="p-3 lg:p-4 flex-1 flex items-center"><h4 className="text-16-tamil-700 lg:text-18-tamil-700 text-1B1B1B">{linkedStory_0.headline}</h4></div></a>;
}
function _temp7(ref, refIdx) {
  return <li key={ref.id || refIdx} className="text-14-tamil-400 text-1B1B1B">{ref.metadata?.url ? <a href={ref.metadata.url} target="_blank" rel="noopener noreferrer" className="text-012061 hover:underline">{ref.metadata?.name || "Reference"}</a> : <span>{ref.metadata?.name}</span>}{ref.metadata?.description && <span className="block text-12-tamil-400" style={{
      color: "#00000099"
    }}>{ref.metadata.description}</span>}</li>;
}
function _temp6(img_1, imgIdx) {
  return img_1["image-s3-key"] && <div key={img_1.id || imgIdx}><img src={getImageUrl(img_1["image-s3-key"])} alt={img_1["alt-text"] || img_1.title || "Gallery image"} className="w-full h-[150px] object-cover rounded" />{img_1["image-attribution"] && <p className="text-12-tamil-400 mt-1" style={{
      color: "#00000099"
    }}>{sanitizeToText(img_1["image-attribution"])}</p>}</div>;
}
function _temp5(img_0) {
  return {
    id: img_0.id,
    title: img_0.title,
    imageS3Key: img_0["image-s3-key"],
    imageMetadata: img_0["image-metadata"],
    attribution: img_0["image-attribution"]
  };
}
function _temp4(img) {
  return img["image-s3-key"];
}
function _temp3(card_0) {
  return card_0["story-elements"]?.filter(_temp2) || [];
}
function _temp2(el_0) {
  return el_0.type === "jsembed" && el_0["embed-js"];
}
function _temp(tag) {
  return {
    name: tag.name,
    slug: tag.slug
  };
}